import React, {
    createContext,
    useContext,
    useEffect, useRef,
    useState,
    useCallback
} from 'react';
import * as signalR from '@microsoft/signalr';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import { get, getFullUrl } from '../hooks/apiFetch';
import { useAuth } from '../Auth/AuthContext';
import DOMPurify from 'dompurify';

const SignalRContext = createContext();
export const useSignalR = () => useContext(SignalRContext);

export const SignalRProvider = ({ children }) => {
    const { isAuthenticated, isLoading, logout, user } = useAuth();
    const connectionRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const isConnectingRef = useRef(false);
    const [connected, setConnected] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info',
        title: ''
    });
    const [forceLogoutDialog, setForceLogoutDialog] = useState(false);
    const isDevelopment = process.env.NODE_ENV === 'development';

    // Refs для хранения данных и интервала
    const loginRef = useRef(null);
    const visitorIdRef = useRef(null);
    const keepAliveIntervalRef = useRef(null);

    const showNotification = (message, severity = 'info', title = '') => {
        setNotification({
            open: true,
            message,
            severity,
            title
        });
    };

    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    const handleDialogClose = (event, reason) => {
        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
        }
        handleForceLogout();
    };

    const handleForceLogout = async () => {
        setForceLogoutDialog(false);
        try {
            await logout();
            showNotification('Вы были вынуждены выйти из системы', 'warning');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const sendUserOnlineStatus = useCallback(async () => {
        try {
            if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
                await connectionRef.current.invoke("UserIsOnline", loginRef.current, visitorIdRef.current);
                if (isDevelopment) {
                    console.log("UserIsOnline status sent at", new Date().toISOString());
                }
            }
        } catch (error) {
            console.error("Error sending UserIsOnline status:", error);
        }
    }, [isDevelopment]);


    const startKeepAliveInterval = useCallback(() => {
        sendUserOnlineStatus();

        keepAliveIntervalRef.current = setInterval(() => {
            sendUserOnlineStatus();
        }, 10000);

        if (isDevelopment) {
            console.log("Keep-alive interval started (10 seconds)");
        }
    }, [sendUserOnlineStatus, isDevelopment]);

    // Функция остановки интервала
    const stopKeepAliveInterval = useCallback(() => {
        if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
            if (isDevelopment) {
                console.log("Keep-alive interval stopped");
            }
        }
    }, [isDevelopment]);

    const containsHtml = (text) => {
        return /<[a-z][\s\S]*>/i.test(text || '');
    };

    const renderNotificationMessage = (message, onLinkClick) => {
        if (!message) {
            return null;
        }

        if (!containsHtml(message)) {
            return message;
        }

        const cleanHtml = DOMPurify.sanitize(message, {
            ALLOWED_TAGS: ['a', 'br', 'b', 'strong'],
            ALLOWED_ATTR: ['href', 'target', 'rel']
        });

        return (
            <span
                className="notification-message"
                onClick={onLinkClick}
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
            />
        );
    };

    async function checkIfJWTIsNew(visitorId) {
        try {
            await get(`/api/auth/IsNewJWT?visitorId=${encodeURIComponent(visitorId)}`,);
            return true;
        } catch (error) {
            console.error('Ошибка при запросе:', error);
            return false;
        }
    }

    useEffect(() => {
        if (isLoading || !isAuthenticated) return;

        const setupConnection = async () => {
            if (isConnectingRef.current) {
                return;
            }
            isConnectingRef.current = true;
            try {
                const signalRUrl = getFullUrl("/tokenHub");

                const fp = await FingerprintJS.load();
                const { visitorId } = await fp.get();

                const login = user?.email;
                if (!login) {
                    console.warn("User email not available, skipping SignalR connection");
                    isConnectingRef.current = false;
                    return;
                }

                loginRef.current = login;
                visitorIdRef.current = visitorId;

                const connection = new signalR.HubConnectionBuilder()
                    .withUrl(signalRUrl, {
                        transport: signalR.HttpTransportType.Auto,
                        withCredentials: true,
                        ignoreNullOptions: true,
                        rejectUnauthorized: false
                    })
                    .configureLogging(isDevelopment ? signalR.LogLevel.Debug : signalR.LogLevel.None)
                    .withAutomaticReconnect()
                    .build();

                connection.on("GetMessages", async (login, changeDescription, additionalData) => {
                    if (isDevelopment) {
                        console.log("Received message:", { login, changeDescription, additionalData });
                    }

                    switch (changeDescription) {
                        case 1:
                            const isNewJWT = await checkIfJWTIsNew(visitorId);
                            if (!isNewJWT) {
                                setForceLogoutDialog(true);
                            } else {
                                setForceLogoutDialog(false);
                                console.log("Выход отключен: JWT создан менее 5 минут назад");
                            }
                            break;

                        case 2:
                            const message = additionalData?.message || 'У вас новое уведомление';
                            const processedMessage = renderNotificationMessage(message);

                            showNotification(
                                processedMessage,
                                additionalData?.severity || 'info',
                                additionalData?.title || 'Уведомление'
                            );
                            break;

                        case 3:
                            showNotification(
                                additionalData?.message || 'Данные были обновлены',
                                'success',
                                'Обновление'
                            );
                            break;

                        case 4:
                            showNotification(
                                additionalData?.message || 'Внимание!',
                                'warning',
                                additionalData?.title || 'Предупреждение'
                            );
                            break;

                        case 5:
                            showNotification(
                                additionalData?.message || 'Произошла ошибка',
                                'error',
                                additionalData?.title || 'Ошибка'
                            );
                            break;

                        case 6:
                            showNotification(
                                additionalData?.message || 'Операция выполнена успешно',
                                'success',
                                additionalData?.title || 'Успех'
                            );
                            break;

                        case 7:
                            showNotification(
                                additionalData?.message || 'Системное сообщение',
                                'info',
                                additionalData?.title || 'Система'
                            );
                            break;

                        default:
                            showNotification(
                                changeDescription,
                                'info',
                                'Сообщение'
                            );
                            break;
                    }
                });

                connection.onclose(() => {
                    setConnected(false);
                    stopKeepAliveInterval();
                    if (isDevelopment) {
                        console.log("SignalR connection closed");
                    }
                });

                connection.onreconnecting(() => {
                    setConnected(false);
                    stopKeepAliveInterval();
                    if (isDevelopment) {
                        console.log("SignalR reconnecting...");
                    }
                });

                connection.onreconnected(() => {
                    setConnected(true);
                    startKeepAliveInterval();
                    if (isDevelopment) {
                        console.log("SignalR reconnected");
                    }
                });

                await connection.start();

                if (isDevelopment) {
                    console.log("SignalR Connected.");
                }

                await connection.invoke("SubscribeToRecord", login);

                connectionRef.current = connection;
                setConnected(true);

                startKeepAliveInterval();

            } catch (err) {
                console.error("SignalR connection failed: ", err);
                stopKeepAliveInterval();
                reconnectTimeoutRef.current = setTimeout(setupConnection, 5000);
            }
            finally {
                isConnectingRef.current = false;
            }
        };

        setupConnection();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            stopKeepAliveInterval();

            if (connectionRef.current) {
                connectionRef.current.off("GetMessages");
                connectionRef.current.stop();
            }
        };
    }, [isAuthenticated, isLoading, logout, isDevelopment, user, startKeepAliveInterval, stopKeepAliveInterval]);

    return (
        <SignalRContext.Provider value={{
            connection: connectionRef.current,
            connected,
            showNotification
        }}>
            {children}

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    variant="outlined"
                >
                    {notification.title && <strong>{notification.title}: </strong>}
                    {notification.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={forceLogoutDialog}
                onClose={handleDialogClose}
                disableEscapeKeyDown
            >
                <DialogTitle>Внимание</DialogTitle>
                <DialogContent>
                    <p>Данные вашей учетной записи были изменены. Для безопасности необходимо выйти из системы и войти снова.</p>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleForceLogout}
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                    >
                        Выйти и войти снова
                    </Button>
                </DialogActions>
            </Dialog>
        </SignalRContext.Provider>
    );
};